import React, { useState, useEffect } from "react";
import { Stack, Typography, Box } from "@mui/material";
import { useTranslation } from "react-i18next";


import { AppConfig } from "Constants";
import { InfoCard } from "../InfoCard";
import { Logo, TaskSquareIcon, DollarCircleIcon } from "./styles";
import TrustedCompanies from "../TrustedCompanies";

import Image02 from "../../../../../assets/img/Image02.png";
import Image03 from "../../../../../assets/img/Image03.png";

const LOGO = AppConfig.appLogo2;

export interface LeftSideWrapperProp {
  accountHasBeenCreated?: boolean;
  variant?: "video" | "image";
  imageSrc?: string;
}

const LeftSideWrapper: React.FC<LeftSideWrapperProp> = ({
  accountHasBeenCreated,
  variant = "video",
  imageSrc = Image03,
}) => {
  const { t: translate } = useTranslation();

  const effectiveVariant = accountHasBeenCreated ? "image" : variant;
  const effectiveImageSrc = accountHasBeenCreated ? Image02 : imageSrc;

  const isVideo = effectiveVariant === "video";

  const [animationStage, setAnimationStage] = useState(0);

  useEffect(() => {
    const stages = [
      { delay: 200, stage: 1 },
      { delay: 400, stage: 2 },
      { delay: 600, stage: 3 },
      { delay: 800, stage: 4 },
      { delay: 1000, stage: 5 },
    ];

    stages.forEach(({ delay, stage }) => {
      setTimeout(() => setAnimationStage(stage), delay);
    });
  }, []);

  const getAnimationStyle = (stage: number) => ({
    opacity: animationStage >= stage ? 1 : 0,
    transform: animationStage >= stage ? "translateY(0)" : "translateY(20px)",
    transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
  });

  return (
    <Box position="relative" height="100%" width="100%" overflow="hidden">
      <video
        src="https://shipeezi-images-dev.s3.ap-southeast-2.amazonaws.com/world_globe_video.mp4"
        autoPlay
        muted
        loop
        playsInline
        style={{
          position: "absolute",
          top: 0,
          left: "-25%",
          width: "130%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "55% center",
          zIndex: 1,
          transform: "scaleX(-1)",
          opacity: isVideo ? 1 : 0,
          transition: "opacity 0.5s ease-in-out",
        }}
      />
      <Box
        component="img"
        src={effectiveImageSrc}
        alt="Shipeezi background"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: 1,
          opacity: !isVideo ? 1 : 0,
          transition: "opacity 0.5s ease-in-out",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 2,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 2.5,
        }}
      />

      <Stack
        direction="column"
        height="100%"
        sx={{
          position: "relative",
          zIndex: 3,
          color: "#fff",
          px: { xs: 4, lg: 6 },
          py: { xs: 5, lg: 7 },
          justifyContent: "space-between",
        }}
      >
        <Stack spacing={{ xs: 4, lg: 5 }} sx={{ pt: { xs: 8, lg: 10 } }}>
          <Box sx={getAnimationStyle(1)}>
            <Logo src={LOGO} alt="Shipeezi" />
          </Box>
          <Stack spacing={{ xs: 2.5, lg: 3 }}>
            <Box sx={getAnimationStyle(2)}>
              <Typography
                fontSize={{ xs: "28px", lg: "32px" }}
                lineHeight={{ xs: "36px", lg: "40px" }}
                fontWeight={700}
                fontFamily="Manrope"
                sx={{ letterSpacing: "-0.02em" }}
              >
                {translate("signup.left.title")}
              </Typography>
            </Box>
            <Box sx={getAnimationStyle(3)}>
              <Typography
                fontSize="16px"
                lineHeight="24px"
                maxWidth="500px"
                fontFamily="Manrope"
                sx={{ opacity: 0.9 }}
              >
                {translate("signup.left.subtitle")}
              </Typography>
            </Box>
          </Stack>
        </Stack>

        <Stack spacing={{ xs: 8, lg: 8 }} sx={{ pb: "97.55px" }}>
          <Box sx={getAnimationStyle(4)}>
            <Stack direction={{ xs: "column", lg: "row" }} spacing={{ xs: 2.5, lg: 3 }}>
              <InfoCard
                icon={<TaskSquareIcon size="21.5" color="#fff" />}
                title={translate("signup.left.card1Title")}
                info={translate("signup.left.card1Desc")}
              />
              <InfoCard
                icon={<DollarCircleIcon size="21.5" color="#fff" />}
                title={translate("signup.left.card2Title")}
                info={translate("signup.left.card2Desc")}
              />
            </Stack>
          </Box>

          <Box sx={getAnimationStyle(5)}>
            <TrustedCompanies />
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
};

export default LeftSideWrapper;
